import { useEffect, useState } from 'react';
import { formatDate } from '@agenz/shared/utils';
import { api } from '../lib/api.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageTitle,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'WON', 'LOST', 'SPAM'];

function LeadRow({ lead, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saving, setSaving] = useState(false);

  async function update(patch) {
    setSaving(true);
    try {
      await onUpdate(lead.id, patch);
    } finally {
      setSaving(false);
    }
  }

  const attribution = [lead.utmSource, lead.utmMedium, lead.utmCampaign]
    .filter(Boolean)
    .join(' / ');

  return (
    <>
      <tr className="border-b border-line">
        <td className="p-4">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-left font-semibold hover:text-electric"
            aria-expanded={open}
          >
            {lead.name}
          </button>
          <p className="text-xs text-muted">{lead.email}</p>
        </td>
        <td className="p-4 text-muted">{lead.company || '—'}</td>
        <td className="p-4 text-xs text-muted">{attribution || 'direct'}</td>
        <td className="p-4 text-xs text-muted">{formatDate(lead.createdAt)}</td>
        <td className="p-4">
          <Select
            value={lead.status}
            disabled={saving}
            onChange={(e) => update({ status: e.target.value })}
            className="w-36 text-xs"
            aria-label={`Status for ${lead.name}`}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-line bg-acid/10">
          <td colSpan={5} className="p-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted">Message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm">{lead.message}</p>

                <dl className="mt-6 grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <dt className="font-bold uppercase tracking-widest text-muted">Budget</dt>
                    <dd className="mt-1">{lead.budget || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-bold uppercase tracking-widest text-muted">Phone</dt>
                    <dd className="mt-1">{lead.phone || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-bold uppercase tracking-widest text-muted">Referrer</dt>
                    <dd className="mt-1 break-all">{lead.referrer || 'direct'}</dd>
                  </div>
                  <div>
                    <dt className="font-bold uppercase tracking-widest text-muted">Spam score</dt>
                    <dd className="mt-1">{Number(lead.spamScore).toFixed(2)}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted">
                  Internal notes
                </p>
                <Textarea
                  className="mt-2"
                  rows={6}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button
                  size="sm"
                  className="mt-3"
                  disabled={saving}
                  onClick={() => update({ notes })}
                >
                  Save notes
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Leads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    api
      .get(`/api/admin/leads?perPage=100${filter ? `&status=${filter}` : ''}`)
      .then(setData)
      .catch((err) => setError(err.message));
  }

  useEffect(load, [filter]);

  async function update(id, patch) {
    try {
      await api.patch(`/api/admin/leads/${id}`, patch);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <PageTitle
        title="Leads"
        description="Every contact form submission, with the campaign that produced it."
        action={
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-44">
            <option value="">All statuses</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        }
      />

      <ErrorNote error={error} onRetry={load} />

      {data && Object.keys(data.byStatus ?? {}).length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {Object.entries(data.byStatus).map(([status, count]) => (
            <Badge key={status} tone={status}>
              {status} {count}
            </Badge>
          ))}
        </div>
      )}

      {!data ? (
        <Spinner />
      ) : data.items.length === 0 ? (
        <EmptyState
          title="No leads yet"
          description="Submissions from the contact form land here, with their UTM attribution attached."
        />
      ) : (
        <Card>
          <table className="w-full text-left text-sm">
            <thead className="border-b-3 border-ink">
              <tr>
                <th className="p-4 text-xs uppercase tracking-widest">Name</th>
                <th className="p-4 text-xs uppercase tracking-widest">Company</th>
                <th className="p-4 text-xs uppercase tracking-widest">Source</th>
                <th className="p-4 text-xs uppercase tracking-widest">Received</th>
                <th className="p-4 text-xs uppercase tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((lead) => (
                <LeadRow key={lead.id} lead={lead} onUpdate={update} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
